import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  ConfirmDeviceCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  UpdateDeviceStatusCommand,
  type DeviceSecretVerifierConfigType,
} from '@aws-sdk/client-cognito-identity-provider'
import CognitoSrpHelper from 'cognito-srp-helper'
import { getTenantId } from '../utils/tenant'
import { viteEnv } from '../utils/viteEnv'

// Get Cognito configuration from environment
// Vite replaces import.meta.env.VITE_* at build time with actual values
// For Jest/testing, import.meta.env is mocked in setupTests.ts
const USER_POOL_ID = viteEnv.VITE_COGNITO_USER_POOL_ID || ''
const CLIENT_ID = viteEnv.VITE_COGNITO_CLIENT_ID || ''

// Get AWS region from environment
const getAwsRegion = (): string => {
  const region = viteEnv.VITE_AWS_REGION
  return region || 'eu-west-1'
}

const cognitoClient = new CognitoIdentityProviderClient({
  region: getAwsRegion(),
})

// Debug logging to help diagnose ClientId issues
if (typeof window !== 'undefined') {
  console.log('[Cognito Service] Configuration:', {
    hasUserPoolId: !!USER_POOL_ID,
    userPoolIdLength: USER_POOL_ID?.length || 0,
    hasClientId: !!CLIENT_ID,
    clientId: CLIENT_ID ? `${CLIENT_ID.substring(0, 8)}...` : 'MISSING',
    region: getAwsRegion(),
  })
  
  if (!CLIENT_ID || CLIENT_ID.trim().length === 0) {
    console.error('[Cognito Service] ❌ ERROR: VITE_COGNITO_CLIENT_ID is not set or empty!')
    console.error('[Cognito Service] Verification codes will fail without a valid ClientId')
    console.error('[Cognito Service] Please rebuild the frontend with the correct VITE_COGNITO_CLIENT_ID environment variable')
  }
}

export interface CognitoLoginCredentials {
  /**
   * Can be either an email address or username
   * Cognito's USERNAME parameter accepts both
   */
  email: string
  password: string
}

export interface CognitoSignUpData {
  email: string
  password: string
  firstName: string
  lastName: string
  salutation?: string
  role?: string
  tenantId?: string
}

export interface CognitoAuthResult {
  accessToken: string
  idToken: string
  refreshToken: string
  expiresIn: number
}

export interface CognitoChallengeResult {
  status: 'challenge_required'
  challengeName: 'EMAIL_OTP'
  session: string
  username: string
  destination?: string
  deviceKey?: string
  deviceGroupKey?: string
}

export interface CognitoAuthenticatedResult {
  status: 'authenticated'
  auth: CognitoAuthResult
  newDeviceKey?: string
  newDeviceGroupKey?: string
}

export type CognitoLoginResult = CognitoChallengeResult | CognitoAuthenticatedResult

export interface CognitoRespondToChallengeData {
  session: string
  username: string
  code: string
  deviceKey?: string
}

export type CognitoDeviceSecretVerifierConfig = DeviceSecretVerifierConfigType

const toCognitoAuthResult = (result: {
  AccessToken?: string
  IdToken?: string
  RefreshToken?: string
  ExpiresIn?: number
}): CognitoAuthResult => {
  if (!result.AccessToken || !result.IdToken) {
    throw new Error('Authentication failed')
  }

  return {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    refreshToken: result.RefreshToken || '',
    expiresIn: result.ExpiresIn || 3600,
  }
}

export const cognitoService = {
  /**
   * Login with email/username and password
   * Note: The 'email' field can contain either an email address or username
   * as Cognito's USERNAME parameter accepts both
   */
  login: async (
    credentials: CognitoLoginCredentials,
    options?: { deviceKey?: string; username?: string; deviceGroupKey?: string; deviceRandomPassword?: string }
  ): Promise<CognitoLoginResult> => {
    if (!USER_POOL_ID || !CLIENT_ID) {
      throw new Error('Cognito is not configured')
    }

    const username = options?.username || credentials.email

    // If we have full remembered-device credentials, run SRP/device challenge flow.
    if (options?.deviceKey && options.deviceGroupKey && options.deviceRandomPassword) {
      const srpSession = CognitoSrpHelper.createSrpSession(username, credentials.password, USER_POOL_ID, false)
      const initiateInput = CognitoSrpHelper.wrapInitiateAuth(srpSession, {
        AuthFlow: 'USER_SRP_AUTH' as const,
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: username,
          DEVICE_KEY: options.deviceKey,
        },
      })
      let response: any = await cognitoClient.send(new InitiateAuthCommand(initiateInput))

      // USER_SRP_AUTH first challenge
      if (response.ChallengeName === 'PASSWORD_VERIFIER' && response.Session) {
        const signedSession = CognitoSrpHelper.signSrpSession(srpSession, response)
        response = await cognitoClient.send(new RespondToAuthChallengeCommand(
          CognitoSrpHelper.wrapAuthChallenge(signedSession, {
            ClientId: CLIENT_ID,
            ChallengeName: 'PASSWORD_VERIFIER',
            Session: response.Session,
            ChallengeResponses: {
              USERNAME: username,
              DEVICE_KEY: options.deviceKey,
            },
          })
        ))
      }

      // Device SRP sub-flow for remembered device
      if (response.ChallengeName === 'DEVICE_SRP_AUTH' && response.Session) {
        const signedSession = CognitoSrpHelper.signSrpSession(srpSession, response)
        response = await cognitoClient.send(new RespondToAuthChallengeCommand(
          CognitoSrpHelper.wrapAuthChallenge(signedSession, {
            ClientId: CLIENT_ID,
            ChallengeName: 'DEVICE_SRP_AUTH',
            Session: response.Session,
            ChallengeResponses: {
              USERNAME: username,
              DEVICE_KEY: options.deviceKey,
            },
          })
        ))
      }

      if (response.ChallengeName === 'DEVICE_PASSWORD_VERIFIER' && response.Session) {
        const signedSessionWithDevice = CognitoSrpHelper.signSrpSessionWithDevice(
          srpSession,
          response,
          options.deviceGroupKey,
          options.deviceRandomPassword
        )
        response = await cognitoClient.send(new RespondToAuthChallengeCommand(
          CognitoSrpHelper.wrapAuthChallenge(signedSessionWithDevice, {
            ClientId: CLIENT_ID,
            ChallengeName: 'DEVICE_PASSWORD_VERIFIER',
            Session: response.Session,
            ChallengeResponses: {
              USERNAME: username,
              DEVICE_KEY: options.deviceKey,
            },
          })
        ))
      }

      if (response.ChallengeName && response.Session) {
        if (response.ChallengeName !== 'EMAIL_OTP') {
          throw new Error(`Unsupported authentication challenge: ${response.ChallengeName}`)
        }
        return {
          status: 'challenge_required',
          challengeName: 'EMAIL_OTP',
          session: response.Session,
          username: response.ChallengeParameters?.USER_ID_FOR_SRP || username,
          destination: response.ChallengeParameters?.CODE_DELIVERY_DESTINATION,
          deviceKey: response.ChallengeParameters?.DEVICE_KEY || options.deviceKey,
          deviceGroupKey: response.ChallengeParameters?.DEVICE_GROUP_KEY || options.deviceGroupKey,
        }
      }

      if (!response.AuthenticationResult) {
        throw new Error('Authentication failed')
      }

      return {
        status: 'authenticated',
        auth: toCognitoAuthResult(response.AuthenticationResult),
        newDeviceKey: response.AuthenticationResult.NewDeviceMetadata?.DeviceKey,
        newDeviceGroupKey: response.AuthenticationResult.NewDeviceMetadata?.DeviceGroupKey,
      }
    }

    const authParameters: Record<string, string> = {
      USERNAME: username,
      PASSWORD: credentials.password,
    }
    if (options?.deviceKey) {
      authParameters.DEVICE_KEY = options.deviceKey
    }

    const command = new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: authParameters,
    })

    const response = await cognitoClient.send(command)

    if (response.ChallengeName && response.Session) {
      if (response.ChallengeName !== 'EMAIL_OTP') {
        throw new Error(`Unsupported authentication challenge: ${response.ChallengeName}`)
      }

      return {
        status: 'challenge_required',
        challengeName: 'EMAIL_OTP',
        session: response.Session,
        username: response.ChallengeParameters?.USER_ID_FOR_SRP || credentials.email,
        destination: response.ChallengeParameters?.CODE_DELIVERY_DESTINATION,
        deviceKey: response.ChallengeParameters?.DEVICE_KEY,
        deviceGroupKey: response.ChallengeParameters?.DEVICE_GROUP_KEY,
      }
    }

    if (!response.AuthenticationResult) {
      throw new Error('Authentication failed')
    }

    return {
      status: 'authenticated',
      auth: toCognitoAuthResult(response.AuthenticationResult),
      newDeviceKey: response.AuthenticationResult.NewDeviceMetadata?.DeviceKey,
      newDeviceGroupKey: response.AuthenticationResult.NewDeviceMetadata?.DeviceGroupKey,
    }
  },

  respondToChallenge: async (
    challengeData: CognitoRespondToChallengeData
  ): Promise<CognitoAuthenticatedResult> => {
    if (!CLIENT_ID) {
      throw new Error('Cognito is not configured')
    }

    const challengeResponses: Record<string, string> = {
      USERNAME: challengeData.username,
      EMAIL_OTP_CODE: challengeData.code,
    }

    if (challengeData.deviceKey) {
      challengeResponses.DEVICE_KEY = challengeData.deviceKey
    }

    const command = new RespondToAuthChallengeCommand({
      ClientId: CLIENT_ID,
      ChallengeName: 'EMAIL_OTP',
      Session: challengeData.session,
      ChallengeResponses: challengeResponses,
    })

    const response = await cognitoClient.send(command)

    if (!response.AuthenticationResult) {
      throw new Error('Verification failed')
    }

    return {
      status: 'authenticated',
      auth: toCognitoAuthResult(response.AuthenticationResult),
      newDeviceKey: response.AuthenticationResult.NewDeviceMetadata?.DeviceKey,
      newDeviceGroupKey: response.AuthenticationResult.NewDeviceMetadata?.DeviceGroupKey,
    }
  },

  confirmDevice: async (
    accessToken: string,
    deviceKey: string,
    deviceName?: string,
    deviceSecretVerifierConfig?: CognitoDeviceSecretVerifierConfig
  ): Promise<boolean> => {
    const command = new ConfirmDeviceCommand({
      AccessToken: accessToken,
      DeviceKey: deviceKey,
      DeviceName: deviceName || 'Trusted browser',
      DeviceSecretVerifierConfig: deviceSecretVerifierConfig,
    })
    const response = await cognitoClient.send(command)
    return response.UserConfirmationNecessary !== true
  },

  rememberDevice: async (accessToken: string, deviceKey: string): Promise<void> => {
    const command = new UpdateDeviceStatusCommand({
      AccessToken: accessToken,
      DeviceKey: deviceKey,
      DeviceRememberedStatus: 'remembered',
    })
    await cognitoClient.send(command)
  },

  /**
   * Sign up a new user
   */
  signUp: async (data: CognitoSignUpData): Promise<void> => {
    if (!USER_POOL_ID || !CLIENT_ID) {
      throw new Error('Cognito is not configured')
    }

    const tenantId = data.tenantId || getTenantId() || ''

    const command = new SignUpCommand({
      ClientId: CLIENT_ID,
      Username: data.email,
      Password: data.password,
      UserAttributes: [
        { Name: 'email', Value: data.email },
        { Name: 'given_name', Value: data.firstName },
        { Name: 'family_name', Value: data.lastName },
        ...(data.salutation ? [{ Name: 'custom:salutation', Value: data.salutation }] : []),
        ...(tenantId ? [{ Name: 'custom:tenantId', Value: tenantId }] : []),
        ...(data.role ? [{ Name: 'custom:role', Value: data.role }] : []),
      ],
    })

    await cognitoClient.send(command)
  },

  /**
   * Confirm sign up with verification code
   */
  confirmSignUp: async (email: string, code: string): Promise<void> => {
    if (!CLIENT_ID) {
      throw new Error('Cognito is not configured')
    }

    const command = new ConfirmSignUpCommand({
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
    })

    await cognitoClient.send(command)
  },

  /**
   * Get current user information
   */
  getCurrentUser: async (accessToken: string): Promise<any> => {
    const command = new GetUserCommand({
      AccessToken: accessToken,
    })

    const response = await cognitoClient.send(command)

    // Extract user attributes
    const attributes: Record<string, string> = {}
    response.UserAttributes?.forEach((attr) => {
      if (attr.Name && attr.Value) {
        attributes[attr.Name] = attr.Value
      }
    })

    return {
      username: response.Username,
      email: attributes.email,
      salutation: attributes['custom:salutation'],
      firstName: attributes.given_name,
      lastName: attributes.family_name,
      tenantId: attributes['custom:tenantId'],
      role: attributes['custom:role'],
      attributes,
    }
  },

  /**
   * Logout (global sign out)
   */
  logout: async (accessToken: string): Promise<void> => {
    const command = new GlobalSignOutCommand({
      AccessToken: accessToken,
    })

    await cognitoClient.send(command)
  },

  /**
   * Initiate forgot password flow
   * Sends a verification code to the user's email
   */
  forgotPassword: async (username: string): Promise<void> => {
    if (!CLIENT_ID || CLIENT_ID.trim().length === 0) {
      console.error('[Cognito Service] forgotPassword called but CLIENT_ID is missing')
      throw new Error('Cognito ClientId is not configured. Please contact support.')
    }

    console.log('[Cognito Service] Initiating forgot password for:', username, 'with ClientId:', `${CLIENT_ID.substring(0, 8)}...`)
    
    const command = new ForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: username, // Can be username or email
    })

    try {
      await cognitoClient.send(command)
      console.log('[Cognito Service] Forgot password code sent successfully')
    } catch (error: any) {
      console.error('[Cognito Service] Forgot password error:', error.message, 'Code:', error.name)
      // Provide more helpful error messages
      if (error.name === 'NotAuthorizedException') {
        throw new Error('User not found. Please check your username or email.')
      } else if (error.name === 'InvalidParameterException') {
        throw new Error('Invalid username or email format.')
      } else if (error.name === 'ResourceNotFoundException') {
        throw new Error('Authentication service not configured correctly. Please contact support.')
      }
      throw error
    }
  },

  /**
   * Confirm forgot password with verification code
   * Resets the user's password
   */
  confirmForgotPassword: async (
    username: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<void> => {
    if (!CLIENT_ID || CLIENT_ID.trim().length === 0) {
      console.error('[Cognito Service] confirmForgotPassword called but CLIENT_ID is missing')
      throw new Error('Cognito ClientId is not configured. Please contact support.')
    }

    console.log('[Cognito Service] Confirming forgot password for:', username, 'with ClientId:', `${CLIENT_ID.substring(0, 8)}...`)
    
    const command = new ConfirmForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: username,
      ConfirmationCode: confirmationCode,
      Password: newPassword,
    })

    try {
      await cognitoClient.send(command)
      console.log('[Cognito Service] Password reset confirmed successfully')
    } catch (error: any) {
      console.error('[Cognito Service] Confirm forgot password error:', error.message, 'Code:', error.name)
      console.error('[Cognito Service] Used ClientId:', `${CLIENT_ID.substring(0, 8)}...`, 'Username:', username)
      
      // Provide more helpful error messages
      if (error.name === 'CodeMismatchException') {
        throw new Error('Invalid verification code. Please check the code and try again, or request a new code.')
      } else if (error.name === 'ExpiredCodeException') {
        throw new Error('Verification code has expired. Please request a new code.')
      } else if (error.name === 'NotAuthorizedException') {
        throw new Error('Invalid verification code or the code was not sent to this account.')
      } else if (error.name === 'InvalidParameterException') {
        if (error.message.includes('password')) {
          throw new Error('Password does not meet requirements. Password must be at least 8 characters with uppercase, lowercase, and numbers.')
        }
        throw new Error('Invalid parameters. Please check your verification code and password.')
      } else if (error.name === 'UserNotFoundException') {
        throw new Error('User not found. Please check your username or email.')
      } else if (error.name === 'ResourceNotFoundException') {
        throw new Error('Authentication service not configured correctly. Please contact support.')
      }
      throw error
    }
  },
}








